import React, { useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { GIDEON_LAUNCH_ISO, GIDEON_SCHEDULE } from '../../data/gideonSeed';
import { getThirdSaturdayOfMonth, getQuarterlyRevivalDay } from '../../utils/gideonCalendarHelpers';

const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function parseLaunchParts() {
  const [y, m, d] = GIDEON_LAUNCH_ISO.split('-').map(Number);
  return { y, m0: m - 1, d };
}

const GideonCalendar = () => {
  const { t } = useLanguage();
  const launchParts = useMemo(() => parseLaunchParts(), []);
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const year = cursor.getFullYear();
  const monthIndex = cursor.getMonth();

  const monthLabel = cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const gridCells = useMemo(() => {
    const firstDow = new Date(year, monthIndex, 1).getDay();
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push(new Date(year, monthIndex, day));
    }
    return cells;
  }, [year, monthIndex]);

  const prevMonth = () => setCursor(new Date(year, monthIndex - 1, 1));
  const nextMonth = () => setCursor(new Date(year, monthIndex + 1, 1));

  const legend = [
    { cls: 'bg-amber-400', labelKey: 'gideon.calendar.legend.launch' },
    { cls: 'bg-emerald-500', labelKey: 'gideon.calendar.legend.quarterly' },
    { cls: 'bg-gideon-khaki', labelKey: 'gideon.calendar.legend.thirdSat' },
    { cls: 'border-l-4 border-sky-400 bg-white/5', labelKey: 'gideon.calendar.legend.zoom' },
  ];

  return (
    <div className="rounded-2xl border border-gideon-khaki/35 bg-gideon-moss/25 backdrop-blur-sm overflow-hidden shadow-lg">
      <div className="flex items-center justify-between px-4 py-3 bg-gideon-ink/90 border-b border-gideon-khaki/30">
        <button
          type="button"
          onClick={prevMonth}
          className="px-3 py-1.5 rounded-lg text-gideon-parchment hover:bg-white/10 text-sm font-semibold"
          aria-label={t('gideon.calendar.prevMonth', 'Previous month')}
        >
          ←
        </button>
        <h3 className="text-gideon-parchment font-bold text-sm sm:text-base">{monthLabel}</h3>
        <button
          type="button"
          onClick={nextMonth}
          className="px-3 py-1.5 rounded-lg text-gideon-parchment hover:bg-white/10 text-sm font-semibold"
          aria-label={t('gideon.calendar.nextMonth', 'Next month')}
        >
          →
        </button>
      </div>

      <div className="p-3 sm:p-4">
        <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-gideon-sand/90 mb-2">
          {WEEKDAY_LABELS.map((w, i) => (
            <div key={i}>{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {gridCells.map((cell, idx) => {
            if (!cell) return <div key={`pad-${idx}`} className="aspect-square min-h-[2.25rem]" />;
            const dow = cell.getDay();
            const day = cell.getDate();

            const isLaunch =
              year === launchParts.y && monthIndex === launchParts.m0 && day === launchParts.d;

            const thirdSat = getThirdSaturdayOfMonth(year, monthIndex);
            const isThirdSat = thirdSat !== null && day === thirdSat;

            const revivalDay = getQuarterlyRevivalDay(year, monthIndex);
            const isQuarterly =
              GIDEON_SCHEDULE.quarterlyRevivalMonths.includes(monthIndex) &&
              revivalDay !== null &&
              day === revivalDay;

            const isZoomNight = dow === GIDEON_SCHEDULE.zoomWeekday;

            let tone =
              'border border-gideon-khaki/20 bg-gideon-ink/40 text-gideon-parchment hover:bg-gideon-ink/60';
            if (isLaunch) tone = 'bg-amber-500/90 text-gideon-ink font-bold ring-2 ring-amber-300';
            else if (isQuarterly) tone = 'bg-emerald-600/90 text-white font-semibold ring-2 ring-emerald-400/60';
            else if (isThirdSat && !isQuarterly) tone = 'bg-gideon-khaki/90 text-gideon-ink font-semibold';

            const zoomAccent =
              isZoomNight && !isLaunch ? 'border-l-4 border-sky-400 border border-gideon-khaki/20' : '';

            const titleParts = [];
            if (isLaunch) titleParts.push(t('gideon.calendar.tooltip.launch', 'Gideon Force launch'));
            if (isQuarterly) titleParts.push(t('gideon.calendar.tooltip.quarterly', 'Quarterly Gideon Prayer Revival'));
            if (isThirdSat) titleParts.push(t('gideon.calendar.tooltip.thirdSat', 'Hybrid corporate — third Saturday'));
            if (isZoomNight) titleParts.push(t('gideon.calendar.tooltip.zoom', 'Gideon Virtual Zoom 8–9 PM'));

            return (
              <div
                key={`${year}-${monthIndex}-${day}`}
                title={titleParts.join(' · ')}
                className={`aspect-square min-h-[2.25rem] flex flex-col items-center justify-center rounded-lg text-xs sm:text-sm transition-colors ${tone} ${zoomAccent}`}
              >
                <span>{day}</span>
              </div>
            );
          })}
        </div>

        <ul className="mt-4 flex flex-wrap gap-3 justify-center text-[11px] sm:text-xs text-gideon-parchment/90">
          {legend.map((item) => (
            <li key={item.labelKey} className="flex items-center gap-1.5">
              <span className={`inline-block w-3 h-3 rounded-sm shrink-0 ${item.cls}`} />
              <span>{t(item.labelKey)}</span>
            </li>
          ))}
        </ul>

        <p className="mt-4 text-xs text-gideon-sand/85 leading-relaxed text-center">
          {t(
            'gideon.calendar.footerNote',
            'Weekly Zoom is every Thursday. Dates follow your local calendar; confirm times with announcements.'
          )}
        </p>
      </div>
    </div>
  );
};

export default GideonCalendar;
